import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="social-media-autocomplete"
export default class extends Controller {
  static targets = ["search", "results", "list", "template"]
  static values = {
    url: { type: String, default: "/api/v1/social_media_sites" }
  }

  connect() {
    this.timeout = null
    this.nextIndex = Date.now()
    console.log('🎯 Social media autocomplete controller connected!')
    console.log('Search target exists:', this.hasSearchTarget)
    console.log('Results target exists:', this.hasResultsTarget)
    console.log('List target exists:', this.hasListTarget)

    // Show alert to confirm controller is loaded
    if (this.hasSearchTarget) {
      this.searchTarget.style.borderColor = 'green'
      setTimeout(() => {
        this.searchTarget.style.borderColor = ''
      }, 1000)
    }
  }

  search() {
    clearTimeout(this.timeout)

    const query = this.searchTarget.value.trim()
    console.log('Searching for:', query)

    if (query.length < 2) {
      this.hideResults()
      return
    }

    this.timeout = setTimeout(() => {
      console.log('Fetching sites for query:', query)
      this.fetchSites(query)
    }, 300)
  }

  async fetchSites(query) {
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
      console.log('Fetching from URL:', url)

      const response = await fetch(url)
      console.log('Response status:', response.status)

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const sites = await response.json()
      console.log('Sites received:', sites)
      this.displayResults(sites)
    } catch (error) {
      console.error('Error fetching social media sites:', error)
      this.hideResults()
    }
  }

  displayResults(sites) {
    if (sites.length === 0) {
      this.resultsTarget.innerHTML = '<div class="p-3 text-sm text-gray-500">No platforms found</div>'
      this.resultsTarget.classList.remove('hidden')
      return
    }

    this.resultsTarget.innerHTML = sites.map(site => `
      <div class="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0 transition-colors"
           data-action="click->social-media-autocomplete#selectSite"
           data-site-id="${site.id}"
           data-site-name="${site.name}"
           data-site-category="${site.category}"
           data-site-description="${site.description || ''}"
           data-site-url="${site.url || ''}">
        <div class="font-semibold text-sm text-gray-900">${this.escapeHtml(site.name)}</div>
        <div class="text-xs text-gray-600 mt-0.5">${this.escapeHtml(site.category)}</div>
        <div class="text-xs text-gray-500 mt-0.5">${this.escapeHtml(site.description || '')}</div>
      </div>
    `).join('')

    this.resultsTarget.classList.remove('hidden')
  }

  selectSite(event) {
    const element = event.currentTarget
    const siteId = element.dataset.siteId
    const siteName = element.dataset.siteName
    const siteUrl = element.dataset.siteUrl

    this.addSiteToList(siteId, siteName, siteUrl)
    this.searchTarget.value = ''
    this.hideResults()
  }

  addSiteToList(siteId, siteName, siteUrl) {
    const index = this.nextIndex++

    const html = `
      <div class="flex items-start gap-3 p-3 bg-gray-50 rounded border border-gray-200" data-social-media-account-item>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2 mb-2">
            <span class="font-medium text-sm text-gray-900">${this.escapeHtml(siteName)}</span>
            <span class="text-xs text-gray-500">${this.escapeHtml(siteUrl)}</span>
          </div>
          <input type="hidden"
                 name="user[user_social_media_accounts_attributes][${index}][social_media_site_id]"
                 value="${siteId}">
          <input type="text"
                 name="user[user_social_media_accounts_attributes][${index}][account_name]"
                 placeholder="Your username or handle (e.g., @username)"
                 class="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                 data-action="keydown->social-media-autocomplete#handleAccountNameKeydown"
                 required>
        </div>
        <button type="button"
                data-action="click->social-media-autocomplete#removeSite"
                class="text-red-600 hover:text-red-800 transition-colors mt-1">
          <i class="fas fa-times text-sm"></i>
        </button>
      </div>
    `

    this.listTarget.insertAdjacentHTML('beforeend', html)

    // Focus on the newly added account name field
    const newInputs = this.listTarget.querySelectorAll('[data-social-media-account-item] input[type="text"]')
    if (newInputs.length > 0) {
      newInputs[newInputs.length - 1].focus()
    }
  }

  handleAccountNameKeydown(event) {
    // If Enter key is pressed
    if (event.key === 'Enter') {
      event.preventDefault() // Prevent form submission

      const accountNameInput = event.target
      const accountName = accountNameInput.value.trim()

      // Only proceed if account name is not empty
      if (accountName) {
        console.log('Account name entered:', accountName)

        // Clear and focus on the search field
        this.searchTarget.value = ''
        this.searchTarget.focus()
        this.hideResults()

        // Show a brief success indicator
        accountNameInput.style.borderColor = 'green'
        setTimeout(() => {
          accountNameInput.style.borderColor = ''
        }, 1000)
      }
    }
  }

  removeSite(event) {
    const item = event.currentTarget.closest('[data-social-media-account-item]')

    // If this is an existing record, mark it for deletion instead of removing from DOM
    const destroyInput = item.querySelector('input[name$="[_destroy]"]')
    if (destroyInput) {
      destroyInput.value = "1"
      item.style.display = 'none'
    } else {
      // New record, safe to remove from DOM
      item.remove()
    }
  }

  hideResults() {
    this.resultsTarget.classList.add('hidden')
  }

  // Click outside to close results
  disconnect() {
    clearTimeout(this.timeout)
  }

  // Utility function to escape HTML and prevent XSS
  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
